import { CORE_PLUGIN } from "@/core"
import type { ConsolePageOption, ConsolePageService, Plugin, SysConfigService } from "@/plugins"
import { PLUGIN_CONSOLE_PAGE, PLUGIN_SYS_CONFIG } from "@/plugins/builtin/BuiltinPluginSymbol"
import { injectOptional } from "@/tsyringe-extend"
import { defineStore } from "pinia"
import { container, injectable, instanceCachingFactory, registry, singleton } from "tsyringe"
import { watch, type App } from "vue"
import ConsoleLogo from "./components/ConsoleLogo.vue"
import SystemLogo from "./components/SystemLogo.vue"
import UserProfile from "./components/UserProfile.vue"

export interface MiscOptions {
  // 关闭所有功能
  disabled?: boolean
  // default logo click
  defaultLogoRedirect?: string
  // 关闭默认的控制台logo
  disableConsoleLogo?: boolean
  // console logo click
  consoleLogoRedirect?: string
  // 不要增加右上角的菜单项
  disableAddHeadControl?: boolean
  // 用户模版，默认为1
  template: 1 | 2;
}

export const MiscPluginOptionsInjectKey = "MiscPluginOptionsInjectKey"

export const useMiscStore = defineStore("miscellaneous", {
  state: () => ({ template: 1 } as MiscOptions)
})

@singleton()
@registry([
  {
    token: CORE_PLUGIN,
    useFactory: instanceCachingFactory<Plugin<MiscOptions, unknown>>((c) => c.resolve(MiscPlugin))
  }
])
@injectable()
export class MiscPlugin implements Plugin<MiscOptions, unknown> {
  id: string | symbol = "";
  options: MiscOptions
  service: unknown
  constructor(
    @injectOptional(MiscPluginOptionsInjectKey)
    options?: MiscOptions
  ) {
    const store = useMiscStore()
    this.options = store.$state
    if (options) {
      store.$patch(options)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  init(app: App<Element>): void {
    if (!this.options.disabled) {
      app.component("IotaDefaultLogo", SystemLogo)
      app.component("IotaDefaultUserProfileControl", UserProfile)
      const plugin = container.resolve<Plugin<ConsolePageOption, ConsolePageService>>(PLUGIN_CONSOLE_PAGE)
      if (!this.options.disableConsoleLogo) {
        app.component("IotaConsoleLogo", ConsoleLogo)
        plugin.service.setLeftLogo("iota-console-logo")
      }
      if (!this.options.disableAddHeadControl) {
        plugin.service.addHeadRight("iota-default-user-profile-control")
      }
      // 根据系统配置，联动刷新系统的参数
      const system = container.resolve<Plugin<unknown, SysConfigService>>(PLUGIN_SYS_CONFIG)
      // 设置title
      const title = system.service.value("title")
      watch(title, () => {
        const t = title.value
        document.title = t ? t.value || "FLASHBASE" : "FLASHBASE"
      })
      // 设置 favorite icon
      const favicon = system.service.value("favicon")
      watch(favicon, () => {
        const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement("link")
        link.rel = "icon"
        const f = favicon.value
        link.href = f ? f.value || link.href : link.href
        document.getElementsByTagName("head")[0].appendChild(link)
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialized(app: App<Element>): void { }
}
